/**
 * Poker Coach AI Service
 * 
 * Uses Google Gemini AI to provide poker strategy advice,
 * hand analysis, pot odds calculations, and educational explanations.
 */

import { VisibleGameState, Card, HandRank, ValidAction, BettingRound } from '../types';
import { AWS_CONFIG } from '../../aws/config';

export interface PokerAdvice {
  recommendation: 'fold' | 'check' | 'call' | 'raise';
  confidence: 'low' | 'medium' | 'high';
  reasoning: string;
  potOdds?: string;
  handStrength?: string;
}

export interface HandAnalysis {
  handName: string;
  strength: 'weak' | 'marginal' | 'good' | 'strong' | 'premium';
  outs: number;
  potOdds: string;
  explanation: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

export class PokerCoachAI {
  private apiEndpoint: string;
  private useProxy: boolean;
  private apiKey: string;
  private conversationHistory: ChatMessage[] = [];

  constructor(apiKey?: string) {
    this.apiEndpoint = AWS_CONFIG.gemini.proxyEndpoint;
    this.useProxy = !!this.apiEndpoint;
    this.apiKey = apiKey || '';

    if (!this.useProxy && !this.apiKey) {
      console.info('PokerCoachAI: No proxy endpoint or API key configured. Using local analysis mode.');
    }
  }

  /**
   * Get strategic advice for the current situation
   */
  async getAdvice(
    gameState: VisibleGameState,
    playerId: string,
    validActions: ValidAction[]
  ): Promise<PokerAdvice> {
    // Use local analysis if no API configured
    if (!this.useProxy && !this.apiKey) {
      return this.getLocalAdvice(gameState, playerId, validActions);
    }

    const context = this.buildPokerContext(gameState, playerId);
    const actionsStr = validActions.map(a => {
      if (a.type === 'raise') return `raise (min: ${a.minAmount}, max: ${a.maxAmount})`;
      if (a.type === 'call') return `call (${a.callAmount})`;
      return a.type;
    }).join(', ');

    const prompt = `You are a poker coach advising a player in Texas Hold'em.

${context}

Available actions: ${actionsStr}

Provide strategic advice. Consider:
1. Hand strength relative to the board
2. Pot odds and implied odds
3. Position and number of players remaining
4. Betting patterns this hand

Respond in JSON format:
{
  "recommendation": "fold" | "check" | "call" | "raise",
  "confidence": "low" | "medium" | "high",
  "reasoning": "2-3 sentence explanation of why this action is best",
  "potOdds": "brief pot odds calculation if relevant",
  "handStrength": "brief description of hand strength"
}`;

    try {
      const response = await this.callGeminiAPI(prompt);
      return this.parseAdviceResponse(response);
    } catch (error) {
      console.error('PokerCoachAI: Error getting advice:', error);
      return this.getLocalAdvice(gameState, playerId, validActions);
    }
  }

  /**
   * Analyze the current hand and position
   */
  async analyzeHand(
    gameState: VisibleGameState,
    playerId: string
  ): Promise<HandAnalysis> {
    // Use local analysis if no API configured
    if (!this.useProxy && !this.apiKey) {
      return this.getLocalHandAnalysis(gameState, playerId);
    }

    const context = this.buildPokerContext(gameState, playerId);

    const prompt = `You are a poker coach analyzing a hand in Texas Hold'em.

${context}

Provide a hand analysis covering:
1. What hand the player currently has (or is drawing to)
2. How strong it is in this situation
3. How many outs they have to improve (if applicable)
4. The pot odds they're getting

Respond in JSON format:
{
  "handName": "name of the hand (e.g., 'Top Pair, Ace kicker')",
  "strength": "weak" | "marginal" | "good" | "strong" | "premium",
  "outs": number of outs to improve (0 if already strong),
  "potOdds": "X:Y or percentage",
  "explanation": "2-3 sentence analysis of the situation"
}`;

    try {
      const response = await this.callGeminiAPI(prompt);
      return this.parseHandAnalysis(response);
    } catch (error) {
      console.error('PokerCoachAI: Error analyzing hand:', error);
      return this.getLocalHandAnalysis(gameState, playerId);
    }
  }

  /**
   * Answer a user question about poker strategy
   */
  async askQuestion(
    question: string,
    gameState: VisibleGameState,
    playerId: string
  ): Promise<string> {
    // Use local answers if no API configured
    if (!this.useProxy && !this.apiKey) {
      return this.getLocalAnswer(question, gameState, playerId);
    }

    const context = this.buildPokerContext(gameState, playerId);

    const prompt = `You are a friendly poker coach. The student asks: "${question}"

Current game situation:
${context}

Give a clear, helpful answer in 2-4 sentences. Use poker terminology but explain it simply. Be specific to their current situation when possible.`;

    try {
      const response = await this.callGeminiAPI(prompt);

      this.conversationHistory.push({ role: 'user', content: question, timestamp: Date.now() });
      this.conversationHistory.push({ role: 'assistant', content: response, timestamp: Date.now() });

      if (this.conversationHistory.length > 20) {
        this.conversationHistory = this.conversationHistory.slice(-20);
      }

      return response;
    } catch (error) {
      console.error('PokerCoachAI: Error answering question:', error);
      return "I'm having trouble connecting right now. Please try again.";
    }
  }

  /**
   * Explain a poker concept
   */
  async explainConcept(concept: string): Promise<string> {
    // Always try local first for concepts (they're static knowledge)
    const localExplanation = this.getLocalConceptExplanation(concept);
    if (localExplanation && !localExplanation.includes('important poker concept')) {
      return localExplanation;
    }

    if (!this.useProxy && !this.apiKey) {
      return localExplanation;
    }

    const prompt = `You are a poker coach explaining a concept to a beginner.

Explain "${concept}" in simple terms:
- What it means
- Why it matters in poker
- A quick example

Keep it to 3-4 sentences. Be clear and practical.`;

    try {
      return await this.callGeminiAPI(prompt);
    } catch (error) {
      return this.getLocalConceptExplanation(concept);
    }
  }

  // ---------------------------------------------------------------------------
  // Context Building
  // ---------------------------------------------------------------------------

  private buildPokerContext(gameState: VisibleGameState, playerId: string): string {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) return 'No player context available.';

    const activePlayers = gameState.players.filter(p => p.status === 'active' || p.status === 'all-in');
    const totalPot = gameState.pots.reduce((sum, p) => sum + p.amount, 0);

    let context = `=== POKER SITUATION ===\n`;
    context += `Betting Round: ${gameState.currentBettingRound || 'pre-flop'}\n`;
    context += `Hand #${gameState.handNumber}\n\n`;

    // Player's cards
    context += `Your Hole Cards: ${this.cardsToString(player.holeCards)}\n`;
    context += `Community Cards: ${gameState.communityCards.length > 0 ? this.cardsToString(gameState.communityCards) : '(none yet)'}\n\n`;

    // Position info
    context += `Your Position: Seat ${player.seatIndex}`;
    if (player.isDealer) context += ' (Dealer/Button)';
    if (player.isSmallBlind) context += ' (Small Blind)';
    if (player.isBigBlind) context += ' (Big Blind)';
    context += '\n';

    // Stack and pot
    context += `Your Stack: ${player.chipStack} chips\n`;
    context += `Your Current Bet: ${player.currentBet}\n`;
    context += `Total Pot: ${totalPot} chips\n`;
    context += `Active Players: ${activePlayers.length}\n\n`;

    // Opponents info
    context += `Opponents:\n`;
    activePlayers.filter(p => p.id !== playerId).forEach(opp => {
      let posInfo = '';
      if (opp.isDealer) posInfo = ' (Dealer)';
      if (opp.isSmallBlind) posInfo = ' (SB)';
      if (opp.isBigBlind) posInfo = ' (BB)';
      context += `  - ${opp.name}${posInfo}: ${opp.chipStack} chips, bet ${opp.currentBet}, status: ${opp.status}\n`;
    });

    // Recent actions
    if (gameState.actionHistory.length > 0) {
      const recentActions = gameState.actionHistory.slice(-6);
      context += `\nRecent Actions:\n`;
      recentActions.forEach(action => {
        const actorName = gameState.players.find(p => p.id === action.playerId)?.name || 'Unknown';
        context += `  - ${actorName}: ${action.action}${action.amount ? ` ${action.amount}` : ''}\n`;
      });
    }

    return context;
  }

  private cardsToString(cards: Card[]): string {
    if (!cards || cards.length === 0) return '(hidden)';
    const suitSymbols: Record<string, string> = {
      hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠'
    };
    return cards.map(c => `${c.rank}${suitSymbols[c.suit] || c.suit}`).join(' ');
  }

  // ---------------------------------------------------------------------------
  // API Communication
  // ---------------------------------------------------------------------------

  private async callGeminiAPI(prompt: string): Promise<string> {
    if (this.useProxy) {
      return this.callViaProxy(prompt);
    } else if (this.apiKey) {
      return this.callDirectAPI(prompt);
    }
    throw new Error('No API endpoint configured');
  }

  private async callViaProxy(prompt: string): Promise<string> {
    const response = await fetch(this.apiEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt,
        config: { temperature: 0.7, maxOutputTokens: 500, topP: 0.8 }
      })
    });

    if (!response.ok) {
      throw new Error(`Proxy error: ${response.status}`);
    }

    const data = await response.json();
    return data.text || '';
  }

  private async callDirectAPI(prompt: string): Promise<string> {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${this.apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          generationConfig: { temperature: 0.7, maxOutputTokens: 500, topP: 0.8 }
        })
      }
    );

    if (!response.ok) throw new Error(`Gemini API error: ${response.status}`);
    const data = await response.json();
    return data.candidates?.[0]?.content?.parts?.[0]?.text || '';
  }

  // ---------------------------------------------------------------------------
  // Response Parsing
  // ---------------------------------------------------------------------------

  private parseAdviceResponse(response: string): PokerAdvice {
    try {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          recommendation: parsed.recommendation || 'check',
          confidence: parsed.confidence || 'medium',
          reasoning: parsed.reasoning || 'Consider your options carefully.',
          potOdds: parsed.potOdds,
          handStrength: parsed.handStrength
        };
      }
    } catch (e) { /* fall through */ }

    return {
      recommendation: 'check',
      confidence: 'low',
      reasoning: response.slice(0, 200) || 'Unable to parse advice.',
    };
  }

  private parseHandAnalysis(response: string): HandAnalysis {
    try {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          handName: parsed.handName || 'Unknown',
          strength: parsed.strength || 'marginal',
          outs: parsed.outs || 0,
          potOdds: parsed.potOdds || 'N/A',
          explanation: parsed.explanation || 'Analysis unavailable.'
        };
      }
    } catch (e) { /* fall through */ }

    return {
      handName: 'Unknown',
      strength: 'marginal',
      outs: 0,
      potOdds: 'N/A',
      explanation: response.slice(0, 200) || 'Unable to analyze hand.'
    };
  }

  // ---------------------------------------------------------------------------
  // Local Fallback (when API is unavailable)
  // ---------------------------------------------------------------------------

  private getLocalAdvice(
    gameState: VisibleGameState,
    playerId: string,
    validActions: ValidAction[]
  ): PokerAdvice {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player || player.holeCards.length < 2) {
      return { recommendation: 'check', confidence: 'low', reasoning: 'Waiting for cards.' };
    }

    const hasCheck = validActions.some(a => a.type === 'check');
    const potSize = gameState.pots.reduce((sum, p) => sum + p.amount, 0);
    const handStrength = this.estimateHandStrength(player.holeCards, gameState.communityCards);

    if (handStrength >= 0.8) {
      return { recommendation: 'raise', confidence: 'high', reasoning: 'You have a very strong hand. Raise to build the pot and extract value.', handStrength: 'Premium' };
    } else if (handStrength >= 0.6) {
      return { recommendation: hasCheck ? 'raise' : 'call', confidence: 'medium', reasoning: 'Solid hand. Consider raising for value or calling to see more cards.', handStrength: 'Strong' };
    } else if (handStrength >= 0.4) {
      return { recommendation: hasCheck ? 'check' : 'call', confidence: 'medium', reasoning: 'Decent hand but not strong enough to raise. Check or call to see more cards cheaply.', handStrength: 'Marginal' };
    } else {
      return { recommendation: hasCheck ? 'check' : 'fold', confidence: 'medium', reasoning: 'Weak hand. Check if free, otherwise fold unless the price is very cheap.', handStrength: 'Weak' };
    }
  }

  private getLocalHandAnalysis(gameState: VisibleGameState, playerId: string): HandAnalysis {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player || player.holeCards.length < 2) {
      return { handName: 'No cards', strength: 'weak', outs: 0, potOdds: 'N/A', explanation: 'Waiting for cards to be dealt.' };
    }

    const strength = this.estimateHandStrength(player.holeCards, gameState.communityCards);
    const potSize = gameState.pots.reduce((sum, p) => sum + p.amount, 0);

    let strengthLabel: HandAnalysis['strength'] = 'weak';
    if (strength >= 0.8) strengthLabel = 'premium';
    else if (strength >= 0.6) strengthLabel = 'strong';
    else if (strength >= 0.4) strengthLabel = 'good';
    else if (strength >= 0.25) strengthLabel = 'marginal';

    return {
      handName: this.describeHand(player.holeCards, gameState.communityCards),
      strength: strengthLabel,
      outs: 0,
      potOdds: potSize > 0 ? `Pot: ${potSize}` : 'N/A',
      explanation: `Your hand is ${strengthLabel}. ${gameState.communityCards.length === 0 ? 'Pre-flop — evaluate based on hole card strength.' : 'Consider how the board connects with your cards.'}`
    };
  }

  private estimateHandStrength(holeCards: Card[], communityCards: Card[]): number {
    if (holeCards.length < 2) return 0;

    const rankValues: Record<string, number> = {
      '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7,
      '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14
    };

    const r1 = rankValues[holeCards[0].rank] || 0;
    const r2 = rankValues[holeCards[1].rank] || 0;
    const suited = holeCards[0].suit === holeCards[1].suit;
    const paired = holeCards[0].rank === holeCards[1].rank;

    // Simple pre-flop hand strength estimation
    let strength = 0;

    if (paired) {
      strength = 0.5 + (Math.max(r1, r2) / 14) * 0.4; // Pairs: 0.5-0.9
    } else {
      const highCard = Math.max(r1, r2);
      const gap = Math.abs(r1 - r2);
      strength = (highCard / 14) * 0.4 + (suited ? 0.1 : 0) - (gap > 4 ? 0.1 : 0);
    }

    // Boost if community cards help (simplified)
    if (communityCards.length > 0) {
      const allCards = [...holeCards, ...communityCards];
      const ranks = allCards.map(c => rankValues[c.rank]);
      const suits = allCards.map(c => c.suit);

      // Check for pairs on board
      const rankCounts = new Map<number, number>();
      ranks.forEach(r => rankCounts.set(r, (rankCounts.get(r) || 0) + 1));
      const maxCount = Math.max(...rankCounts.values());

      if (maxCount >= 4) strength = Math.max(strength, 0.9);
      else if (maxCount >= 3) strength = Math.max(strength, 0.75);
      else if (maxCount >= 2) strength = Math.max(strength, 0.5);

      // Check for flush potential
      const suitCounts = new Map<string, number>();
      suits.forEach(s => suitCounts.set(s, (suitCounts.get(s) || 0) + 1));
      const maxSuitCount = Math.max(...suitCounts.values());
      if (maxSuitCount >= 5) strength = Math.max(strength, 0.85);
      else if (maxSuitCount >= 4) strength = Math.max(strength, 0.55);
    }

    return Math.min(1, Math.max(0, strength));
  }

  private describeHand(holeCards: Card[], communityCards: Card[]): string {
    if (holeCards.length < 2) return 'No cards';

    const r1 = holeCards[0].rank;
    const r2 = holeCards[1].rank;
    const suited = holeCards[0].suit === holeCards[1].suit;

    if (r1 === r2) return `Pocket ${r1}s`;
    const high = r1 > r2 ? r1 : r2;
    const low = r1 > r2 ? r2 : r1;
    return `${high}-${low}${suited ? ' suited' : ' offsuit'}`;
  }

  private getLocalAnswer(question: string, gameState: VisibleGameState, playerId: string): string {
    const q = question.toLowerCase();
    const player = gameState.players.find(p => p.id === playerId);
    const strength = player ? this.estimateHandStrength(player.holeCards, gameState.communityCards) : 0;
    const potSize = gameState.pots.reduce((sum, p) => sum + p.amount, 0);
    const activePlayers = gameState.players.filter(p => p.status === 'active' || p.status === 'all-in').length;

    if (q.includes('bluff')) {
      if (activePlayers <= 2 && potSize > 0) {
        return 'Bluffing works best against fewer opponents. With only 2 players active, a well-timed bluff can be effective — especially if the board looks scary for your opponent. Consider your table image and how your opponent has been playing.';
      }
      return 'Bluffing is risky with multiple opponents — someone is likely to call. Save bluffs for heads-up situations or when the board strongly favors your perceived range.';
    }

    if (q.includes('out')) {
      if (gameState.communityCards.length === 0) {
        return "Pre-flop, outs aren't as relevant yet. Focus on your starting hand strength and position. After the flop, count cards that improve your hand — each out gives roughly 2% chance per remaining card.";
      }
      return `With ${gameState.communityCards.length} community cards showing, count how many unseen cards improve your hand. Multiply outs by 2 for one card to come, or by 4 for two cards. For example, a flush draw has 9 outs ≈ 18% on the turn or 36% with two cards to come.`;
    }

    if (q.includes('pot odd')) {
      if (potSize > 0 && player) {
        const callAmount = gameState.players.find(p => p.currentBet > (player.currentBet || 0))
          ? Math.max(...gameState.players.map(p => p.currentBet)) - (player.currentBet || 0)
          : 0;
        if (callAmount > 0) {
          const odds = (potSize / callAmount).toFixed(1);
          return `The pot is ${potSize} and you'd need to call ${callAmount}, giving you ${odds}:1 pot odds. You need to win more than ${(100 / (parseFloat(odds) + 1)).toFixed(0)}% of the time for a call to be profitable.`;
        }
      }
      return 'Pot odds = pot size ÷ cost to call. If the pot is 100 and you call 20, you get 5:1 odds. Compare this to your chance of winning — if your equity is higher than the odds require, calling is profitable.';
    }

    if (q.includes('position')) {
      if (player?.isDealer) {
        return "You're on the button — the best position! You act last on every post-flop street, giving you maximum information. You can play more hands and control the pot size more effectively.";
      }
      if (player?.isBigBlind) {
        return "You're in the big blind. You've already invested chips, so you get a discount to see the flop. Post-flop you'll be out of position (acting first), so be selective with marginal hands.";
      }
      if (player?.isSmallBlind) {
        return "You're in the small blind — the worst position. You act second-to-last pre-flop but first post-flop. Play tight here and don't defend too widely.";
      }
      return `You're in seat ${player?.seatIndex || '?'}. Generally, later positions are better because you see more information before acting. Play tighter in early position and wider in late position.`;
    }

    if (q.includes('fold') || q.includes('should i')) {
      if (strength < 0.25) {
        return 'Your hand is weak. Unless you can check for free, folding is usually the right play. Save your chips for better spots.';
      } else if (strength >= 0.6) {
        return "You have a strong hand — don't fold! Consider raising to build the pot and protect against draws.";
      }
      return 'With a marginal hand, it depends on the price. If you can see cards cheaply, it might be worth it. If facing a big bet, folding is often correct.';
    }

    if (q.includes('raise') || q.includes('bet')) {
      if (strength >= 0.7) {
        return 'With a strong hand, raising is usually correct. Bet for value — you want weaker hands to call. A good sizing is 50-75% of the pot.';
      }
      return 'Raise when you have a strong hand (for value) or when you think opponents will fold (as a bluff). Standard raise sizes are 2.5-3x the big blind pre-flop, or 50-75% of the pot post-flop.';
    }

    // Default response based on hand strength
    if (strength >= 0.7) {
      return `You have a strong hand (estimated ${Math.round(strength * 100)}% strength). Look for ways to build the pot — raise or re-raise if possible. Don't slow-play too much with multiple opponents.`;
    } else if (strength >= 0.4) {
      return `Your hand is decent (estimated ${Math.round(strength * 100)}% strength). Play cautiously — check or call small bets, but don't commit too many chips without improvement.`;
    }
    return `Your hand is on the weaker side (estimated ${Math.round(strength * 100)}% strength). Look for free cards or cheap draws. Don't invest heavily unless the situation improves.`;
  }

  private getLocalConceptExplanation(concept: string): string {
    const concepts: Record<string, string> = {
      'pot odds': 'Pot odds compare the current pot size to the cost of calling. If the pot is 100 and you need to call 20, your pot odds are 5:1. If your chance of winning is better than 1 in 6 (about 17%), calling is profitable long-term.',
      'position': 'Position refers to when you act in a betting round. Acting last (being "in position") is a huge advantage because you see what everyone else does before deciding. The dealer button is the best position.',
      'bluff': 'A bluff is betting or raising with a weak hand to make opponents fold better hands. Good bluffs tell a believable story — your betting pattern should look like you have a strong hand. Bluff less against calling stations.',
      'outs': 'Outs are cards remaining in the deck that will improve your hand. For example, if you have 4 cards to a flush, there are 9 remaining cards of that suit (13 - 4 = 9 outs). Multiply outs by 2 for turn odds, or by 4 for turn+river odds.',
      'implied odds': 'Implied odds account for money you expect to win on future streets if you hit your draw. Even if pot odds don\'t justify a call now, if you\'ll win a big pot when you hit, the call can be profitable.',
      'hand ranges': 'A hand range is the set of possible hands an opponent could have based on their actions. A tight player raising pre-flop might have AA-TT and AK-AQ. Thinking in ranges rather than specific hands is key to advanced play.',
    };

    const key = Object.keys(concepts).find(k => concept.toLowerCase().includes(k));
    return key ? concepts[key] : `${concept} is an important poker concept. In general, understanding the mathematical and psychological aspects of poker will improve your game significantly.`;
  }

  /**
   * Get conversation history
   */
  getHistory(): ChatMessage[] {
    return [...this.conversationHistory];
  }

  /**
   * Clear conversation history
   */
  clearHistory(): void {
    this.conversationHistory = [];
  }
}
